import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams } from "expo-router";
import type { ReactNode } from "react";
import { useContext, useEffect, useRef, useState } from "react";
import {
  type ColorValue,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  View as RNView,
  TextInput,
} from "react-native";
import Animated, {
  FadeIn,
  FadeOut,
  useAnimatedRef,
} from "react-native-reanimated";
import { View, useTheme } from "tamagui";

import { LayoutContext, ScrollView, Text, YStack } from "@/components";
import AutoplayAccordion from "@/components/Accordion";
import ChatInput from "@/components/ChatInput";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useCurrentConversationId } from "@/hooks/conversation";
import { UseCase, useUseCaseSuggestions } from "@/hooks/suggestions";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import useRefsToTrackScrollProgress from "@/hooks/useRefsToTrackScrollProgress";
import LegalDisclaimer from "@/landing-page/general-care/LegalDisclaimer";
import StickyChatButton from "@/landing-page/StickyChatButton";
import UseCasePrompts from "@/landing-page/UseCasePrompts";
import ClickableCarousel from "@/landing-page/v3/ClickableCarousel";
import { setTag } from "@/sentry";
import { HERO_BY_USE_CASE } from "../HeroWithCTA";
import AtAGlance from "./AtAGlance";
import FAQ from "./FAQ";
import SectionHeader from "./SectionHeader";
import Testimonial from "./Testimonial";

const SECTION_NAMES = [
  "at-a-glance",
  "user-testimonial",
  "carousel-1",
  "curai-testimonial",
  "autoplay-accordion",
  "carousel-2",
  "faq",
  "footer",
];

export default function UseCaseLayoutV3() {
  const conversationId = useCurrentConversationId();
  const { value: usesPaymentProgression } = useFeatureFlag(
    "chai-paywall-user-progression",
    false,
  );
  const { useCase, utm_term } = useLocalSearchParams();
  const theme = useTheme();

  const useCaseString = (
    Array.isArray(useCase) ? (useCase?.[0] ?? "") : (useCase ?? "")
  ) as UseCase;

  const {
    headerV3,
    firstCarouselV3,
    secondCarouselV3,
    autoplayAccordionV3,
    atAGlanceV3,
    userTestimonialV3,
    curaiTestimonialV3,
    faqV3,
  } = useUseCaseSuggestions(useCaseString);

  useEffect(() => {
    setTag("context.use_case", useCaseString);
    setTag("context.came_from_ads", Boolean(utm_term));
  }, [useCaseString, utm_term]);
  const isSignedInStable = useIsSignedInStable();
  const { headerHeight } = useContext(LayoutContext);
  const [scrollY, setScrollY] = useState(0);

  const scrollRef = useAnimatedRef<Animated.ScrollView>();
  const secondSectionRef = useRef<RNView>(null);
  const chatInputRef = useRef<TextInput>(null);
  const showLandingPage = !isSignedInStable && !conversationId;

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrollY(e.nativeEvent.contentOffset.y);
  };

  const sectionRefs = useRefsToTrackScrollProgress(SECTION_NAMES, {
    "at-a-glance": secondSectionRef,
  });

  const hero = HERO_BY_USE_CASE[useCaseString];
  const heroGradientColors: [ColorValue, ColorValue] = [
    theme.fillHeroGradientStart?.get(),
    theme.fillHeroGradientEnd?.get(),
  ];
  const { lg } = useContext(LayoutContext);

  let topOfPage: ReactNode;
  if (!hero) {
    topOfPage = (
      <View
        backgroundColor="$backgroundFill"
        height="auto"
        $lg={{ height: "85%", justifyContent: "center" }}
        width="100%"
        alignItems="stretch"
      >
        <Animated.View
          entering={FadeIn.duration(150)}
          exiting={FadeOut.duration(150)}
        >
          <YStack
            marginTop={headerHeight.current}
            gap="$400"
            maxWidth="$constraint"
            width="100%"
            alignSelf="center"
            paddingHorizontal="$200"
            paddingTop="$300"
            $lg={{ paddingTop: 0 }}
          >
            <Text size="$xxl" $lg={{ size: 40, semibold: true }} semibold>
              {headerV3?.headline}
            </Text>
            <View gap="$400">
              <UseCasePrompts v3 />
              <ChatInput
                key={`chat-input-landing-${useCaseString}`}
                dataTrackingId={`chat-input-landing-${useCaseString}`}
                inputRef={chatInputRef}
              />
            </View>
          </YStack>
        </Animated.View>
      </View>
    );
  } else if (lg) {
    topOfPage = (
      <LinearGradient
        start={{ x: 0, y: 1 }}
        end={{ x: 1, y: 1 }}
        colors={heroGradientColors}
      >
        {hero}
      </LinearGradient>
    );
  } else {
    topOfPage = <View>{hero}</View>;
  }

  return (
    <>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{ height: "100%" }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        backgroundColor="$fillModal"
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {topOfPage}
        {showLandingPage && (
          <>
            <View
              width="100%"
              backgroundColor="$fillModal"
              paddingTop="$500"
              paddingBottom="$800"
              $lg={{ paddingTop: "$800", paddingBottom: 100 }}
            >
              <AtAGlance ref={sectionRefs["at-a-glance"]} {...atAGlanceV3!} />
            </View>
            {userTestimonialV3 && (
              <View
                width="100%"
                backgroundColor="$backgroundFill"
                paddingTop="$500"
                paddingBottom="$800"
                $lg={{ paddingTop: "$1000", paddingBottom: 160 }}
              >
                <Testimonial
                  includeShimmerTag
                  ref={sectionRefs["user-testimonial"]}
                  {...userTestimonialV3!}
                />
              </View>
            )}
            {firstCarouselV3 && (
              <View
                width="100%"
                backgroundColor="$fillModal"
                paddingTop="$500"
                paddingBottom="$800"
                $lg={{ paddingTop: "$800", paddingBottom: 140 }}
              >
                <SectionHeader
                  marginBottom="$400"
                  $lg={{ textAlign: "center", marginBottom: "$800" }}
                  paddingHorizontal="$250"
                  textInPrimaryColor={firstCarouselV3!.headerTextInPrimaryColor}
                  textInBrandColor={firstCarouselV3!.headerTextInBrandColor}
                  ref={sectionRefs["carousel-1"]}
                />
                <ClickableCarousel
                  cardColor="$contentLoaderBackground"
                  items={firstCarouselV3!.items}
                />
              </View>
            )}
            {curaiTestimonialV3 && (
              <View
                width="100%"
                backgroundColor="$backgroundFill"
                paddingTop="$500"
                paddingBottom="$800"
                $lg={{ paddingTop: "$1000", paddingBottom: 160 }}
              >
                <Testimonial
                  includeShimmerTag={false}
                  ref={sectionRefs["curai-testimonial"]}
                  {...curaiTestimonialV3!}
                />
              </View>
            )}

            {autoplayAccordionV3 && (
              <YStack
                width="100%"
                backgroundColor="$fillModal"
                paddingTop="$500"
                paddingBottom="$800"
                $lg={{ paddingTop: "$800", paddingBottom: 140 }}
              >
                <View
                  paddingHorizontal="$250"
                  maxWidth="$landingPageConstraint"
                  alignSelf="center"
                >
                  <SectionHeader
                    textInPrimaryColor={
                      autoplayAccordionV3.headerTextInPrimaryColor
                    }
                    textInBrandColor={
                      autoplayAccordionV3.headerTextInBrandColor
                    }
                    marginBottom="$500"
                    $lg={{ textAlign: "center", marginBottom: "$800" }}
                    ref={sectionRefs["autoplay-accordion"]}
                  />
                  <AutoplayAccordion
                    scrollRef={scrollRef}
                    scrollY={scrollY}
                    drawers={autoplayAccordionV3.drawers}
                  />
                </View>
              </YStack>
            )}

            {secondCarouselV3 && (
              <View
                width="100%"
                backgroundColor="$backgroundFill"
                paddingTop="$500"
                paddingBottom="$800"
                $lg={{ paddingTop: "$800", paddingBottom: 140 }}
              >
                <SectionHeader
                  $lg={{ textAlign: "center" }}
                  paddingHorizontal="$250"
                  paddingBottom="$500"
                  textInPrimaryColor={secondCarouselV3.headerTextInPrimaryColor}
                  textInBrandColor={secondCarouselV3.headerTextInBrandColor}
                  ref={sectionRefs["carousel-2"]}
                />
                <ClickableCarousel
                  cardColor="$fillModal"
                  items={secondCarouselV3.items}
                />
              </View>
            )}

            <View
              width="100%"
              backgroundColor="$fillModal"
              paddingTop="$500"
              paddingBottom="$800"
              $lg={{ paddingTop: "$800", paddingBottom: 140 }}
            >
              <FAQ
                ref={sectionRefs["faq"]}
                items={faqV3!}
                paymentsEnabled={usesPaymentProgression}
              />
            </View>

            <RNView ref={sectionRefs.footer}>
              <LegalDisclaimer backgroundColor={null} />
            </RNView>
          </>
        )}
      </ScrollView>
      {showLandingPage && !hero && (
        <StickyChatButton
          scrollRef={scrollRef}
          secondSectionRef={secondSectionRef}
          scrollY={scrollY}
          chatInputRef={chatInputRef}
        />
      )}
    </>
  );
}
